/**
 * @file App.tsx
 * @summary Racine de l'application
 *
 * Déclare le router React Router, les providers globaux (TanStack Query, Tooltip, Toaster) et la liste des routes (utilisée par le plugin auto-sitemap).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
import FloatingActions from "./components/FloatingActions";
import InstallPwaBanner from "./components/InstallPwaBanner";

// Toasters lazy-loadés : ~6 KiB JS retirés du chunk initial (sonner + radix-toast).
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

const NotFound = lazy(() => import("./pages/NotFound"));
const MentionsLegales = lazy(() => import("./pages/legal/MentionsLegales"));
const CGU = lazy(() => import("./pages/legal/CGU"));
const CGV = lazy(() => import("./pages/legal/CGV"));
const RGPD = lazy(() => import("./pages/legal/RGPD"));
const CookiesPage = lazy(() => import("./pages/legal/CookiesPage"));
const Lexique = lazy(() => import("./pages/Lexique"));
const ChargesPage = lazy(() => import("./pages/ChargesPage"));
const RdvServices = lazy(() => import("./pages/RdvServices"));
const CoursWOD = lazy(() => import("./pages/services/CoursWOD"));
const HybridTraining = lazy(() => import("./pages/services/HybridTraining"));
const CrossfitDebutant = lazy(() => import("./pages/services/CrossfitDebutant"));
const ReprendreLeSport = lazy(() => import("./pages/services/ReprendreLeSport"));
const LieuxHub = lazy(() => import("./pages/lieux/LieuxHub"));
const CommunePage = lazy(() => import("./pages/lieux/CommunePage"));
const QueRecherchezVousCrossfit = lazy(() => import("./pages/blog/QueRecherchezVousCrossfit"));
const QuestCeQueLeCrossfit = lazy(() => import("./pages/blog/QuestCeQueLeCrossfit"));
const CrossfitSenior = lazy(() => import("./pages/blog/CrossfitSenior"));
const CrossfitCoupleDebutant = lazy(() => import("./pages/blog/CrossfitCoupleDebutant"));
const AncienSportifCrossfit = lazy(() => import("./pages/blog/AncienSportifCrossfit"));
const CrossfitEnfants = lazy(() => import("./pages/blog/CrossfitEnfants"));
const CrossfitScienceSante = lazy(() => import("./pages/blog/CrossfitScienceSante"));
const PrixCrossfitBenchmark = lazy(() => import("./pages/blog/PrixCrossfitBenchmark"));
const GuideCompletCrossfit = lazy(() => import("./pages/blog/GuideCompletCrossfit"));
const SportSaintPierreChandieu = lazy(() => import("./pages/blog/SportSaintPierreChandieu"));
const CompetitionsCrossfitOpen = lazy(() => import("./pages/blog/CompetitionsCrossfitOpen"));
const AvisCrossfitChistera = lazy(() => import("./pages/blog/AvisCrossfitChistera"));
const CalculateurChargesCrossfit = lazy(() => import("./pages/blog/CalculateurChargesCrossfit"));
const SeanceCrossfit = lazy(() => import("./pages/blog/SeanceCrossfit"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const DouleurGenouCrossfit = lazy(() => import("./pages/blog/DouleurGenouCrossfit"));
const ReprendreSportApresLigamentCroise = lazy(() => import("./pages/blog/ReprendreSportApresLigamentCroise"));
const DouleurEpauleCrossfit = lazy(() => import("./pages/blog/DouleurEpauleCrossfit"));
const FaireDuSportAvecDouleur = lazy(() => import("./pages/blog/FaireDuSportAvecDouleur"));
const ReprendreLeSportApresBlessure = lazy(() => import("./pages/blog/ReprendreLeSportApresBlessure"));
const MalDeDosSportCrossfit = lazy(() => import("./pages/blog/MalDeDosSportCrossfit"));
const SportApres35AnsCadreActif = lazy(() => import("./pages/blog/SportApres35AnsCadreActif"));
const TendinopathieSportCrossfit = lazy(() => import("./pages/blog/TendinopathieSportCrossfit"));
const DouleurHancheRetourSport = lazy(() => import("./pages/blog/DouleurHancheRetourSport"));
const DouleurCoudeCrossfit = lazy(() => import("./pages/blog/DouleurCoudeCrossfit"));
const DouleurCervicaleSport = lazy(() => import("./pages/blog/DouleurCervicaleSport"));
const CoachingIndividuelCompetition = lazy(() => import("./pages/blog/CoachingIndividuelCompetition"));
const CoachingIndividuelObjectifs = lazy(() => import("./pages/blog/CoachingIndividuelObjectifs"));
const EvenementsCrossfitHyrox = lazy(() => import("./pages/blog/EvenementsCrossfitHyrox"));
const CoachsChisteraSpecialites = lazy(() => import("./pages/blog/CoachsChisteraSpecialites"));
const CompetiteursDebutantsMemeBox = lazy(() => import("./pages/blog/CompetiteursDebutantsMemeBox"));
const ReprendreSportApresOpHaut = lazy(() => import("./pages/blog/ReprendreSportApresOpHaut"));
const ReprendreSportApresOpBas = lazy(() => import("./pages/blog/ReprendreSportApresOpBas"));
const ReprendreSportApresAccident = lazy(() => import("./pages/blog/ReprendreSportApresAccident"));
const ConseilsCoachsReussirWod = lazy(() => import("./pages/blog/ConseilsCoachsReussirWod"));
const AdherentsCrossfitChistera = lazy(() => import("./pages/blog/AdherentsCrossfitChistera"));
const CoachsChisteraEquipe = lazy(() => import("./pages/blog/CoachsChisteraEquipe"));
const EvenementsInternesChistera = lazy(() => import("./pages/blog/EvenementsInternesChistera"));
const TemoignageCadresParisRecommencer = lazy(() => import("./pages/blog/TemoignageCadresParisRecommencer"));
const MarkdownArticle = lazy(() => import("./pages/blog/MarkdownArticle"));
import { EXTRA_ARTICLES } from "./data/blog-extra-articles";

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Handle PWA shortcut "?action=reserver" → smooth-scroll to #contact on home. */
function PwaShortcutHandler() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (pathname !== "/") return;
    const params = new URLSearchParams(search);
    if (params.get("action") === "reserver") {
      window.setTimeout(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, [pathname, search]);
  return null;
}




const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <PwaShortcutHandler />
        <FloatingActions />
        <InstallPwaBanner />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/legal/mentions" element={<MentionsLegales />} />
            <Route path="/legal/cgu" element={<CGU />} />
            <Route path="/legal/cgv" element={<CGV />} />
            <Route path="/legal/rgpd" element={<RGPD />} />
            <Route path="/legal/cookies" element={<CookiesPage />} />
            <Route path="/lexique" element={<Lexique />} />
            <Route path="/charges" element={<ChargesPage />} />
            <Route path="/rdv-services" element={<RdvServices />} />
            <Route path="/cours/wod" element={<CoursWOD />} />
            <Route path="/cours/hybrid-training" element={<HybridTraining />} />
            <Route path="/crossfit-debutant" element={<CrossfitDebutant />} />
            <Route path="/reprendre-le-sport" element={<ReprendreLeSport />} />
            <Route path="/lieux" element={<LieuxHub />} />
            <Route path="/lieux/:slug" element={<CommunePage />} />
            <Route path="/blog/que-recherchez-vous-dans-le-crossfit" element={<QueRecherchezVousCrossfit />} />
            <Route path="/blog/quest-ce-que-le-crossfit" element={<QuestCeQueLeCrossfit />} />
            <Route path="/blog/crossfit-senior" element={<CrossfitSenior />} />
            <Route path="/blog/crossfit-couple-debutant" element={<CrossfitCoupleDebutant />} />
            <Route path="/blog/ancien-sportif-crossfit" element={<AncienSportifCrossfit />} />
            <Route path="/blog/crossfit-enfants" element={<CrossfitEnfants />} />
            <Route path="/blog/crossfit-science-sante-benefices" element={<CrossfitScienceSante />} />
            <Route path="/blog/prix-crossfit-lyon-est-benchmark" element={<PrixCrossfitBenchmark />} />
            <Route path="/blog/guide-complet-crossfit" element={<GuideCompletCrossfit />} />
            <Route path="/blog/faire-du-sport-saint-pierre-de-chandieu" element={<SportSaintPierreChandieu />} />
            <Route path="/blog/competitions-crossfit-open-lyon" element={<CompetitionsCrossfitOpen />} />
            <Route path="/blog/avis-crossfit-chistera" element={<AvisCrossfitChistera />} />
            <Route path="/blog/calculateur-charges-crossfit-1rm" element={<CalculateurChargesCrossfit />} />
            <Route path="/blog/comment-se-passe-une-seance-crossfit" element={<SeanceCrossfit />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/douleur-genou-crossfit" element={<DouleurGenouCrossfit />} />
            <Route path="/blog/reprendre-sport-apres-ligament-croise" element={<ReprendreSportApresLigamentCroise />} />
            <Route path="/blog/douleur-epaule-crossfit" element={<DouleurEpauleCrossfit />} />
            <Route path="/blog/faire-du-sport-avec-douleur" element={<FaireDuSportAvecDouleur />} />
            <Route path="/blog/reprendre-le-sport-apres-blessure" element={<ReprendreLeSportApresBlessure />} />
            <Route path="/blog/mal-de-dos-sport-crossfit" element={<MalDeDosSportCrossfit />} />
            <Route path="/blog/sport-apres-35-ans-cadre-actif" element={<SportApres35AnsCadreActif />} />
            <Route path="/blog/tendinopathie-sport-crossfit" element={<TendinopathieSportCrossfit />} />
            <Route path="/blog/douleur-hanche-retour-sport" element={<DouleurHancheRetourSport />} />
            <Route path="/blog/douleur-coude-crossfit" element={<DouleurCoudeCrossfit />} />
            <Route path="/blog/douleur-cervicale-sport" element={<DouleurCervicaleSport />} />
            <Route path="/blog/coaching-individuel-competition-hyrox-athx-crossfit" element={<CoachingIndividuelCompetition />} />
            <Route path="/blog/coaching-individuel-objectifs-sportifs" element={<CoachingIndividuelObjectifs />} />
            <Route path="/blog/evenements-crossfit-hyrox-france-region" element={<EvenementsCrossfitHyrox />} />
            <Route path="/blog/coachs-crossfit-chistera-specialites" element={<CoachsChisteraSpecialites />} />
            <Route path="/blog/competiteurs-crossfit-chistera-debutants" element={<CompetiteursDebutantsMemeBox />} />
            <Route path="/blog/reprendre-sport-apres-operation-haut-du-corps" element={<ReprendreSportApresOpHaut />} />
            <Route path="/blog/reprendre-sport-apres-operation-bas-du-corps" element={<ReprendreSportApresOpBas />} />
            <Route path="/blog/reprendre-sport-apres-accident" element={<ReprendreSportApresAccident />} />
            <Route path="/blog/conseils-coachs-reussir-wod-crossfit" element={<ConseilsCoachsReussirWod />} />
            <Route path="/blog/adherents-crossfit-chistera" element={<AdherentsCrossfitChistera />} />
            <Route path="/blog/coachs-crossfit-chistera-equipe" element={<CoachsChisteraEquipe />} />
            <Route path="/blog/evenements-internes-chistera-communaute" element={<EvenementsInternesChistera />} />
            <Route path="/blog/temoignage-adherents-crossfit-est-lyonnais" element={<TemoignageCadresParisRecommencer />} />
            {EXTRA_ARTICLES.map((a) => (
              <Route key={a.slug} path={`/blog/${a.slug}`} element={<MarkdownArticle slug={a.slug} />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
